import { z } from 'zod'
import { zDate, zEmail } from '../zod-utils.js'

/**
 * Normalised form used for the client-side name filter. Lower-cased, whitespace
 * collapsed, and Thai/Latin punctuation dropped so "สมชาย  ใจดี" finds "สมชายใจดี".
 */
export const searchKeyOf = (name: string): string =>
  name
    .normalize('NFC')
    .toLowerCase()
    .replace(/[\s.,'"()\-_]/g, '')

export const ParticipantSchema = z.object({
  id: z.string().trim().min(1),
  name: z.string().trim().min(1).max(160),
  searchKey: z.string().min(1),
  /*
   * Optional, and only ever a convenience: when the organiser already holds the
   * address, the buyer should not have to type it — and an address typed by nobody
   * cannot be mistyped. It is never a password: picking the name does not prove you
   * are that person, so this fills the field and the buyer can still change it.
   */
  email: zEmail.optional(),
  /**
   * Soft delete. A name removed from the roster is hidden, not erased, because the
   * orders that reference it are still real and someone will ask about them. A
   * cleanup job can hard-delete after the event is archived.
   */
  deletedAt: zDate.nullable().optional(),
})
export type Participant = z.infer<typeof ParticipantSchema>

export const isActiveParticipant = (participant: Participant): boolean => !participant.deletedAt

/** Whitespace is collapsed on the way in, so "สมชาย   ใจดี" and "สมชาย ใจดี" are one person. */
export const cleanParticipantName = (name: string): string => name.trim().replace(/\s+/g, ' ')

/**
 * The id is derived, never passed in.
 *
 * It used to take one, and the seed script numbered people by position while the
 * admin importer hashed their name — so re-importing the same roster through the
 * other door marked all twelve as removed and added twelve "new" people with the
 * same names. An id you can supply is an id two callers will supply differently.
 */
export const participantFrom = (name: string, email?: string): Participant => {
  const clean = cleanParticipantName(name)
  const address = email?.trim().toLowerCase()
  return {
    id: participantIdFor(clean),
    name: clean,
    searchKey: searchKeyOf(clean),
    // The id stays derived from the NAME alone, so adding an address to an existing
    // roster updates those people rather than creating a second copy of each.
    ...(address ? { email: address } : {}),
  }
}

/**
 * Deterministic id from the name itself.
 *
 * Re-importing the same roster has to update rows rather than create a second copy
 * of everybody, and a positional id (`p0001`) breaks that the moment someone is
 * inserted in the middle of the list. Hashing the normalised name means the same
 * person keeps the same id no matter where they appear in the file.
 */
export const participantIdFor = (name: string): string => {
  const key = searchKeyOf(name)
  let hash = 0x811c9dc5
  for (let index = 0; index < key.length; index++) {
    hash ^= key.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return `p${hash.toString(36).padStart(7, '0')}`
}

export type ParticipantRow = { name: string; email?: string }

/** Excel paste: tabs, commas and semicolons all separate columns. */
const SPLIT_LINES = /\r?\n/
const SPLIT_COLUMNS = /[\t,;]/
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const unquote = (value: string): string => value.trim().replace(/^["']|["']$/g, '')

/**
 * Is this first line a column heading rather than a person?
 *
 * The downloadable template ships with "ชื่อ-นามสกุล | อีเมล" across the top, and
 * without this that heading imports as somebody called "ชื่อ-นามสกุล" — a phantom on
 * the roster of every organiser who used the template as intended.
 *
 * Only ever applied to the FIRST row, and only when the text is a heading word on
 * its own. Nobody is named "ชื่อ", but somebody might well be named something that
 * merely contains it.
 */
const HEADER_WORDS = /^(ชื่อ[\s\-–]*(นามสกุล|ผู้สั่ง|นักวิ่ง)?|นาม|name|full\s*name|อีเมล|อีเมล์|email|e-mail)$/i

const looksLikeHeader = (first: string, second: string): boolean =>
  HEADER_WORDS.test(first) || (!!second && HEADER_WORDS.test(second))

/**
 * Parses a pasted block into clean, de-duplicated rows.
 *
 * Column one is the name; column two, when there is one, is an e-mail address.
 * People paste straight out of Excel, so tabs, commas and semicolons all separate.
 *
 * Anything in column two that is not an address — a size, a header row saying
 * "อีเมล", a phone number — is DROPPED rather than refused. A roster of three
 * hundred people is not worth rejecting over a header line, and a wrong address
 * silently filled into the order form would be worse than none.
 */
export const parseParticipantRows = (raw: string): ParticipantRow[] => {
  const seen = new Set<string>()
  const rows: ParticipantRow[] = []
  let first = true
  for (const line of raw.split(SPLIT_LINES)) {
    const columns = line.split(SPLIT_COLUMNS)
    const name = cleanParticipantName(unquote(columns[0] ?? ''))
    if (!name) continue

    // Checked against the first row that has any content, not line 1 of the file:
    // a spreadsheet exported with a blank top row still puts its headings second.
    if (first) {
      first = false
      if (looksLikeHeader(name, unquote(columns[1] ?? ''))) continue
    }
    const key = searchKeyOf(name)
    if (!key || seen.has(key)) continue
    seen.add(key)

    const candidate = unquote(columns[1] ?? '').toLowerCase()
    const email = LOOKS_LIKE_EMAIL.test(candidate) ? candidate : undefined
    rows.push(email ? { name, email } : { name })
  }
  return rows
}
